import moment from 'moment';
import 'moment-timezone';
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import ReactJsonPretty from 'react-json-pretty';
import { injectIntl } from 'react-intl';

import {
  ModalHeader,
  ModalBody,
  Card,
  CardBody,
  Button,
  ModalFooter,
  Collapse,
} from 'reactstrap';
import '../../assets/chatlogs-slide-modal.css';
import Pagination from './Pagination';
import { FaCopy, FaCheck } from 'react-icons/fa';

const projectJsonTheme = {
  main: 'background: #f6f8fa; color: #2d3748;',
  key: 'color: #007bff; font-weight: 600;',
  string: 'color: #e83e8c;',
  value: 'color: #20c997;',
  null: 'color: #6c757d;',
  number: 'color: #17a2b8;',
  colon: 'color: #2d3748;',
  comma: 'color: #2d3748;',
  bracket: 'color: #2d3748;',
};

const reactJsonPrettyWrapStyle = `
.react-json-pretty, .react-json-pretty pre, .json-pretty-wrap, .json-pretty-wrap pre {
  white-space: pre-wrap !important;
  word-break: break-word !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
  box-sizing: border-box;
}
`;

// ─── Moved OUTSIDE component so it never gets recreated on render ───
const SectionHeader = ({
  title,
  icon,
  isOpen,
  onToggle,
  data,
  copyKey,
  copiedKey,
  onCopy,
  actionLabel,
  onActionClick,
}) => (
  <button
    type="button"
    onClick={onToggle}
    style={{
      background: 'none',
      border: 'none',
      fontWeight: 600,
      cursor: 'pointer',
      marginBottom: 10,
      marginTop: 10,
      padding: 0,
      fontSize: '1em',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {isOpen ? '▾' : '▸'}
      {typeof icon === 'string' ? (
        <i className={icon} style={{ color: '#007bff' }}></i>
      ) : icon ? (
        icon
      ) : (
        <i className="iconsminds-file" style={{ color: '#007bff' }} />
      )}
      <b>{title}</b>
    </div>
    {actionLabel && (
      <span
        onClick={(e) => {
          e.stopPropagation();
          onActionClick && onActionClick();
        }}
        style={{
          marginLeft: 'auto',
          marginRight: 12,
          fontSize: '0.85em',
          color: 'var(--theme-color-1)',
          cursor: 'pointer',
          textDecoration: 'underline',
          whiteSpace: 'nowrap',
        }}
      >
        {actionLabel}
      </span>
    )}
    <div
      onClick={(e) => {
        e.stopPropagation();
        onCopy(data, copyKey);
      }}
      title={copiedKey === copyKey ? 'Copied!' : 'Copy'}
      style={{ cursor: 'pointer', padding: '4px' }}
    >
      {copiedKey === copyKey ? (
        <FaCheck style={{ color: 'green' }} />
      ) : (
        <FaCopy />
      )}
    </div>
  </button >
);

// ─── Moved OUTSIDE component ───
function highlightContext(context, search) {
  if (!search) {
    return context.split(/\r?\n/).map((line, idx, arr) => (
      <span key={idx}>
        {line}
        {idx < arr.length - 1 && <br />}
      </span>
    ));
  }
  const esc = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(esc, 'gi');
  return context.split(/\r?\n/).map((line, idx, arr) => {
    const parts = line.split(regex);
    const matches = line.match(regex);
    if (!matches) {
      return (
        <span key={idx}>
          {line}
          {idx < arr.length - 1 && <br />}
        </span>
      );
    }
    let result = [];
    for (let i = 0; i < parts.length; i++) {
      result.push(<span key={i + '-part'}>{parts[i]}</span>);
      if (i < matches.length) {
        result.push(
          <span
            key={i + '-match'}
            style={{
              background: '#ffe066',
              color: '#222',
              borderRadius: '3px',
              padding: '0 2px',
            }}
          >
            {matches[i]}
          </span>
        );
      }
    }
    return (
      <span key={idx}>
        {result}
        {idx < arr.length - 1 && <br />}
      </span>
    );
  });
}

// ─── Helper to safely parse JSON ───
function safeParseJson(value) {
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return value;
  }
}

const ChatLogs = ({
  chatLogs,
  isOpen,
  toggle,
  currentPage,
  totalPages,
  onChangePage,
  conversationId,
  timeZone,
}) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [searchTexts, setSearchTexts] = useState({});
  // FIX: initialize as null (not undefined) so logsAreFresh check works correctly
  const [lastConversationId, setLastConversationId] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [showToolsJson, setShowToolsJson] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);

  // FIX: logsAreFresh now also guards on !showLoader
  const logsAreFresh =
    !showLoader &&
    Array.isArray(chatLogs) &&
    chatLogs.length > 0 &&
    lastConversationId !== null &&
    lastConversationId === conversationId;

  const modelName = logsAreFresh && chatLogs[0]?.model ? chatLogs[0].model : '';

  // FIX: clear ALL panel state when conversationId changes (including showToolsJson)
  useEffect(() => {
    setShowLoader(true);
    setOpenIndex(null);
    setSearchTexts({});
    setShowToolsJson({});
  }, [conversationId]);

  // FIX: also clear panel state on page change so stale keys don't bleed across pages
  useEffect(() => {
    setOpenIndex(null);
    setSearchTexts({});
    setShowToolsJson({});
  }, [currentPage]);

  // Escape key closes modal — toggle added to deps to avoid stale closure
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') toggle();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, toggle]);

  // When fresh logs arrive for current conversation, hide loader
  useEffect(() => {
    if (Array.isArray(chatLogs) && chatLogs.length > 0 && conversationId) {
      setLastConversationId(conversationId);
      setShowLoader(false);
    }
  }, [chatLogs, conversationId]);

  const togglePanel = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const toggleSection = (key) => {
    setShowToolsJson((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Whether a section is open — defaults to true (open) unless explicitly set false
  const isSectionOpen = (key) => showToolsJson[key] !== false;

  const handleCopy = (data, key) => {
    const text =
      typeof data === 'string' ? data : JSON.stringify(data, null, 2);

    const fallbackCopy = (t) => {
      const textarea = document.createElement('textarea');
      textarea.value = t;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
      } catch { }
      document.body.removeChild(textarea);
    };

    if (navigator.clipboard && document.hasFocus()) {
      navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }

    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  return (
    <div>
      <style>{reactJsonPrettyWrapStyle}</style>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.45)',
            zIndex: 9998,
            transition: 'opacity 0.3s',
          }}
        />
      )}

      <div
        className={'chatlogs-slide-modal' + (isOpen ? ' open' : '')}
        style={{ display: isOpen ? 'flex' : 'none' }}
      >
        <ModalHeader toggle={toggle}>Chat Logs</ModalHeader>

        <ModalBody>
          {/* FIX: show loader only while genuinely loading */}
          {showLoader ? (
            <div className="loading" />
          ) : logsAreFresh ? (
            <div>
              <h5 className="mb-4">
                <strong>Model:</strong> {modelName ? (modelName === 'gpt-4o-mini' ? 'Azure Open AI (gpt-4o-mini)' : modelName) : ''}
              </h5>

              {chatLogs.map((entry, index) => (
                <Card
                  key={index}
                  className="mb-3 shadow-sm border-0"
                  style={{ borderRadius: '14px', overflow: 'hidden' }}
                >
                  {/* ── Accordion header ── */}
                  <div
                    className={
                      'd-flex align-items-center justify-content-between px-4 py-3 ' +
                      (openIndex === index ? 'bg-light' : 'bg-white') +
                      ' text-dark'
                    }
                    style={{
                      cursor: 'pointer',
                      borderRadius:
                        openIndex === index ? '14px 14px 0 0' : '14px',
                      fontWeight: 600,
                      fontSize: '1rem',
                      transition: 'border-radius 0.2s',
                    }}
                    onClick={() => togglePanel(index)}
                  >
                    <span
                      style={{
                        fontFamily: 'Funnel Sans, Quicksand, Arial, sans-serif',
                        color: '#000',
                        fontWeight: 'normal',
                        flex: 1,
                        display: 'inline-block',
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {entry.query}
                    </span>

                    {entry.createdOn && (
                      <span
                        style={{
                          fontSize: '0.75em',
                          color: '#888',
                          marginLeft: 16,
                          marginRight: 8,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {timeZone
                          ? moment
                            .utc(entry.createdOn)
                            .tz(timeZone)
                            .format('MMM Do, YYYY [at] h:mm a')
                          : moment(entry.createdOn).format(
                            'MMM Do, YYYY [at] h:mm a'
                          )}
                      </span>
                    )}

                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: '20px',
                        color: '#6c757d',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {openIndex === index ? '▾' : '▸'}
                    </span>
                  </div>

                  {/* ── Accordion body ── */}
                  <Collapse isOpen={openIndex === index}>
                    <CardBody
                      className="bg-white text-dark px-3 py-2"
                      style={{
                        borderRadius: '0 0 14px 14px',
                        fontFamily: 'Funnel Sans, Quicksand, Arial, sans-serif',
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                        letterSpacing: '0.01em',
                        position: 'relative',
                        minHeight: 64,
                      }}
                    >
                      {/* ── Rewritten Question ── */}
                      {entry.rewriteQuestion &&
                        entry.rewriteQuestion !== '' && (
                          <div>
                            <SectionHeader
                              title="Rewritten User Question"
                              icon={
                                <span style={{ color: '#007bff' }}>✏️</span>
                              }
                              isOpen={!!showToolsJson['rewrite-' + index]}
                              onToggle={() => toggleSection('rewrite-' + index)}
                              data={entry.rewriteQuestion}
                              copyKey={'rewrite-' + index}
                              copiedKey={copiedKey}
                              onCopy={handleCopy}
                            />
                            {showToolsJson['rewrite-' + index] && (
                              <div
                                style={{
                                  background: '#f6f8fa',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 8,
                                  padding: '10px 16px',
                                  fontSize: '0.95em',
                                  fontFamily:
                                    'Funnel Sans, Quicksand, Arial, monospace',
                                  wordBreak: 'break-all',
                                  maxHeight: 220,
                                  overflowY: 'auto',
                                }}
                              >
                                <pre
                                  style={{
                                    margin: 0,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    color: '#222',
                                  }}
                                >
                                  {entry.rewriteQuestion}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}

                      {/* ── Instructions ── */}
                      {entry.instructions && entry.instructions !== '' && (
                        <div>
                          <SectionHeader
                            title="Conversation Flow Instructions"
                            icon="iconsminds-information"
                            isOpen={!!showToolsJson['instructions-' + index]}
                            onToggle={() =>
                              toggleSection('instructions-' + index)
                            }
                            data={entry.instructions}
                            copyKey={'instructions-' + index}
                            copiedKey={copiedKey}
                            onCopy={handleCopy}
                            actionLabel="Go to conversation flow"
                            onActionClick={() => {
                              window.location.href = '/app/pages/propinfo/recommender';
                            }}
                          />
                          {showToolsJson['instructions-' + index] && (
                            <div
                              style={{
                                background: '#f6f8fa',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                padding: '10px 16px',
                                fontSize: '0.95em',
                                fontFamily:
                                  'Funnel Sans, Quicksand, Arial, monospace',
                                wordBreak: 'break-all',
                                maxHeight: 220,
                                overflowY: 'auto',
                              }}
                            >
                              <pre
                                style={{
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-all',
                                  color: '#222',
                                }}
                              >
                                {entry.instructions}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Tools JSON ── */}
                      {entry.tools && entry.tools !== '' && (
                        <div>
                          <SectionHeader
                            title="Conversation Flow API"
                            icon="iconsminds-gear"
                            isOpen={!!showToolsJson['tools-' + index]}
                            onToggle={() => toggleSection('tools-' + index)}
                            data={entry.tools}
                            copyKey={'tools-' + index}
                            copiedKey={copiedKey}
                            onCopy={handleCopy}
                          />
                          {showToolsJson['tools-' + index] && (
                            <div
                              className="json-pretty-wrap"
                              style={{
                                background: '#f6f8fa',
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                padding: '10px 16px',
                                fontSize: '0.95em',
                                fontFamily:
                                  'Funnel Sans, Quicksand, Arial, monospace',
                                maxHeight: 220,
                                overflowY: 'auto',
                                margin: 0,
                                boxSizing: 'border-box',
                              }}
                            >
                              <ReactJsonPretty
                                data={safeParseJson(entry.tools)}
                                theme={projectJsonTheme}
                                themeClassName="custom-json-pretty"
                                style={{
                                  fontSize: '0.95em',
                                  borderRadius: '8px',
                                  background: '#f6f8fa',
                                  fontFamily:
                                    'Funnel Sans, Quicksand, Arial, monospace',
                                  margin: 0,
                                  maxWidth: '100%',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Knowledge Hub Context ── */}
                      <div>
                        <SectionHeader
                          title="Knowledge Hub Extracted Content"
                          icon="iconsminds-file"
                          isOpen={isSectionOpen('context-' + index)}
                          onToggle={() => toggleSection('context-' + index)}
                          data={entry.context}
                          copyKey={'context-' + index}
                          copiedKey={copiedKey}
                          onCopy={handleCopy}
                        />
                      </div>
                      {isSectionOpen('context-' + index) && (
                        <div className="wrap-text">
                          <div style={{ marginTop: '12px', marginBottom: 8 }}>
                            <input
                              type="text"
                              placeholder="Search context..."
                              value={searchTexts[index] || ''}
                              onChange={(e) =>
                                setSearchTexts((st) => ({
                                  ...st,
                                  [index]: e.target.value,
                                }))
                              }
                              style={{
                                width: '100%',
                                fontSize: '0.85em',
                                padding: '4px 8px',
                                borderRadius: '6px',
                                border: '1px solid #ddd',
                                outline: 'none',
                                marginBottom: 4,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              background: '#f6f8fa',
                              border: '1px solid #e5e7eb',
                              borderRadius: 8,
                              padding: '10px 16px',
                              fontSize: '0.95em',
                              fontFamily:
                                'Funnel Sans, Quicksand, Arial, monospace',
                              maxHeight: '30vh',
                              overflowY: 'auto',
                              margin: 0,
                              boxSizing: 'border-box',
                            }}
                          >
                            {typeof entry.context === 'string'
                              ? highlightContext(
                                entry.context,
                                searchTexts[index] || ''
                              )
                              : entry.context}
                          </div>
                        </div>
                      )}

                      {/* ── History ── */}
                      {(() => {
                        const json = safeParseJson(entry.history);
                        const isEmpty =
                          json === undefined ||
                          json === null ||
                          (typeof json === 'string' && json.trim() === '') ||
                          (Array.isArray(json) && json.length === 0);
                        if (isEmpty) return null;
                        return (
                          <>
                            <div className="wrap-text">
                              <SectionHeader
                                title="History"
                                icon="iconsminds-clock"
                                isOpen={isSectionOpen('history-' + index)}
                                onToggle={() =>
                                  toggleSection('history-' + index)
                                }
                                data={entry.history}
                                copyKey={'history-' + index}
                                copiedKey={copiedKey}
                                onCopy={handleCopy}
                              />
                            </div>
                            {isSectionOpen('history-' + index) && (
                              <div className="wrap-text">
                                <div
                                  style={{
                                    marginTop: '12px',
                                    position: 'static',
                                  }}
                                />
                                <div
                                  className="json-pretty-wrap"
                                  style={{ position: 'static' }}
                                >
                                  <ReactJsonPretty
                                    data={json}
                                    theme={projectJsonTheme}
                                    themeClassName="custom-json-pretty"
                                    style={{
                                      fontSize: '0.95em',
                                      borderRadius: '8px',
                                      padding: '12px 16px',
                                      background: '#f6f8fa',
                                      fontFamily:
                                        'Funnel Sans, Quicksand, Arial, monospace',
                                      margin: 0,
                                      maxWidth: '100%',
                                      position: 'static',
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                      {/* ── Response ── */}
                      <div className="wrap-text">
                        <SectionHeader
                          title="Response"
                          icon="iconsminds-speach-bubble"
                          isOpen={isSectionOpen('response-' + index)}
                          onToggle={() => toggleSection('response-' + index)}
                          data={entry.response}
                          copyKey={'response-' + index}
                          copiedKey={copiedKey}
                          onCopy={handleCopy}
                        />
                      </div>
                      {isSectionOpen('response-' + index) && (
                        <div className="wrap-text">
                          <div style={{ marginTop: '12px' }} />
                          {(() => {
                            const json = safeParseJson(entry.response);
                            if (
                              json &&
                              typeof json === 'object' &&
                              json.arguments
                            ) {
                              return (
                                <div className="json-pretty-wrap">
                                  <ReactJsonPretty
                                    data={json.arguments}
                                    theme={projectJsonTheme}
                                    themeClassName="custom-json-pretty"
                                    style={{
                                      fontSize: '0.95em',
                                      borderRadius: '8px',
                                      padding: '12px 16px',
                                      background: '#f6f8fa',
                                      fontFamily:
                                        'Funnel Sans, Quicksand, Arial, monospace',
                                      margin: 0,
                                      maxWidth: '100%',
                                    }}
                                  />
                                </div>
                              );
                            }
                            return <span className="text-muted"></span>;
                          })()}
                        </div>
                      )}

                      {/* ── Annotations ── */}
                      {Array.isArray(entry.annotation) &&
                        entry.annotation.length > 0 &&
                        entry.annotation.some(
                          (a) => a && a.sourceFile && a.pageNumber != null
                        ) && (
                          <div
                            style={{
                              fontSize: '0.85em',
                              color: '#555',
                              background: '#f6f8fa',
                              border: '1px solid #bbb',
                              borderRadius: 6,
                              padding: '2px 8px',
                              minWidth: 0,
                              textAlign: 'left',
                              display: 'block',
                              marginTop: 8,
                              maxWidth: '60%',
                              wordBreak: 'break-word',
                            }}
                            title="Source file and page references"
                          >
                            {entry.annotation
                              .filter(
                                (a) => a && a.sourceFile && a.pageNumber != null
                              )
                              .map((a, i) => (
                                <span key={i} style={{ display: 'block' }}>
                                  📄 {a.sourceFile}
                                  {a.pageNumber !== 0
                                    ? ` p.${a.pageNumber}`
                                    : ''}
                                </span>
                              ))}
                          </div>
                        )}

                      {/* ── Timing & Tokens ── */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: 12,
                          borderTop: '1px solid #eee',
                          padding: '8px 0 0 0',
                          marginTop: 12,
                          minHeight: 40,
                        }}
                      >
                        {entry.toolName?.trim() && (
                          <div
                            style={{
                              border: '2px solid #888',
                              borderRadius: 6,
                              padding: '2px 10px',
                              fontSize: '0.95em',
                              fontWeight: 600,
                              color: '#444',
                              background: '#f8f9fa',
                              display: 'inline-flex',
                              alignItems: 'center',
                              minWidth: 48,
                              textAlign: 'center',
                              gap: 6,
                            }}
                            title="Conversation Flow API Triggered"
                          >
                            <span role="img" aria-label="Tool">
                              🛠️
                            </span>{' '}
                            {entry.toolName}
                          </div>
                        )}

                        {entry.tokens > 0 && (
                          <div
                            style={{
                              border: '2px solid #888',
                              borderRadius: 6,
                              padding: '2px 10px',
                              fontSize: '0.95em',
                              fontWeight: 600,
                              color: '#444',
                              background: '#f8f9fa',
                              display: 'inline-flex',
                              alignItems: 'center',
                              minWidth: 48,
                              textAlign: 'center',
                              gap: 6,
                            }}
                            title="Tokens consumed"
                          >
                            <span role="img" aria-label="Tokens">
                              🔢
                            </span>{' '}
                            {entry.tokens}
                          </div>
                        )}

                        {entry.searchDocumentTime > 0 && (
                          <div
                            style={{
                              border: '2px solid #888',
                              borderRadius: 6,
                              padding: '2px 10px',
                              fontSize: '0.95em',
                              fontWeight: 600,
                              color: '#444',
                              background: '#f8f9fa',
                              display: 'inline-flex',
                              alignItems: 'center',
                              minWidth: 48,
                              textAlign: 'center',
                              gap: 6,
                            }}
                            title="Time to search Knowledge Hub"
                          >
                            <span role="img" aria-label="Search">
                              🔍
                            </span>{' '}
                            {entry.searchDocumentTime > 999
                              ? (entry.searchDocumentTime / 1000).toFixed(1) +
                              ' s'
                              : entry.searchDocumentTime + ' ms'}
                          </div>
                        )}

                        {entry.llmResponseTime > 0 && (
                          <div
                            style={{
                              border: '2px solid #888',
                              borderRadius: 6,
                              padding: '2px 10px',
                              fontSize: '0.95em',
                              fontWeight: 600,
                              color: '#444',
                              background: '#f8f9fa',
                              display: 'inline-flex',
                              alignItems: 'center',
                              minWidth: 48,
                              textAlign: 'center',
                              gap: 6,
                            }}
                            title="Time to generate response"
                          >
                            <span role="img" aria-label="LLM">
                              🤖
                            </span>{' '}
                            {entry.llmResponseTime > 999
                              ? (entry.llmResponseTime / 1000).toFixed(1) + ' s'
                              : entry.llmResponseTime + ' ms'}
                          </div>
                        )}
                      </div>
                    </CardBody>
                  </Collapse>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center">No logs available</div>
          )}
        </ModalBody>

        <ModalFooter>
          {logsAreFresh && (
            <Pagination
              currentPage={currentPage}
              totalPage={totalPages}
              onChangePage={(i) => onChangePage(i)}
            />
          )}
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </div>
    </div>
  );
};

const mapStateToProps = ({ settings }) => {
  const { timeZone } = settings;
  return { timeZone };
};

export default injectIntl(connect(mapStateToProps, {})(ChatLogs));
